import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import BellIcon from "@/assets/bell.svg";
import { useQuery, useMutation } from "@apollo/client";
import {
  NOTIFICATIONS_QUERY,
  NOTIFICATIONS_MUTATION,
} from "./notification.query";
import {
  NotificationsQueryQuery,
  NotificationsQueryQueryVariables,
  ReadNotificationMutation,
  ReadNotificationMutationVariables,
} from "@/__generated__/graphql";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useState, useRef, useEffect } from "react";
import {
  getEventIdsByMessageType,
  getNotificationDisplay,
} from "@/helpers/notifications";
import { NotificationEventId } from "./notifications.types";
import MarkAllAsRead from "./MarkAllAsRead";
import { NOTIFICATION_PAGINATION_LIMIT } from "@/helpers/constants";
import orderBy from "lodash/orderBy";
import { captureException } from "@sentry/browser";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface NotificationItem {
  node: {
    id: string;
    created: string;
    ui?: {
      id: string;
      url: string;
      textForUrl: string;
      message: string;
    } | null;
    eventId: string;
    isRead: boolean;
  };
}

const themeColorMap: Record<string, string> = {
  red: "border-red-500 ring-red-500 text-red-500",
  yellow: "border-yellow-500 ring-yellow-500 text-yellow-500",
  blue: "border-blue-500 ring-blue-500 text-blue-500",
  green: "border-green-500 ring-green-500 text-green-500",
  orange: "border-orange-500 ring-orange-500 text-orange-500",
  purple: "border-purple-500 ring-purple-500 text-purple-500",
  pink: "border-pink-500 ring-pink-500 text-pink-500",
  gray: "border-gray-500 ring-gray-500 text-gray-500",
  indigo: "border-indigo-500 ring-indigo-500 text-indigo-500",
  violet: "border-violet-500 ring-violet-500 text-violet-500",
  cyan: "border-cyan-500 ring-cyan-500 text-cyan-500",
  teal: "border-teal-500 ring-teal-500 text-teal-500",
  lime: "border-lime-500 ring-lime-500 text-lime-500",
  amber: "border-amber-500 ring-amber-500 text-amber-500",
  rose: "border-rose-500 ring-rose-500 text-rose-500",
};

const NotificationsFetcher: React.FC<{
  eventIds: string[];
  onRefetchReady?: (refetchFn: () => void) => void;
}> = ({ eventIds, onRefetchReady }) => {
  const variables = {
    eventIds:
      eventIds as unknown as NotificationsQueryQueryVariables["eventIds"],
    limit: NOTIFICATION_PAGINATION_LIMIT,
  };

  const { data, refetch, fetchMore, loading, error } = useQuery<
    NotificationsQueryQuery,
    NotificationsQueryQueryVariables
  >(NOTIFICATIONS_QUERY, {
    variables: variables as Partial<NotificationsQueryQueryVariables>,
    errorPolicy: "all",
  });

  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(() => {
        void refetch();
      });
    }
  }, [onRefetchReady, refetch]);

  const [readNotification] = useMutation<
    ReadNotificationMutation,
    ReadNotificationMutationVariables
  >(NOTIFICATIONS_MUTATION);

  const loadMore = async () => {
    if (!data?.me?.notifications?.pageInfo?.startCursor) return;

    await fetchMore({
      variables: {
        ...variables,
        startCursor: data.me.notifications.pageInfo.startCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const pResult = previousResult as NotificationsQueryQuery;
        const nResult = fetchMoreResult as NotificationsQueryQuery;

        if (!nResult.me) return previousResult;

        return {
          me: {
            ...nResult.me,
            notifications: {
              ...nResult.me.notifications,
              edges: [
                ...(pResult.me?.notifications?.edges || []),
                ...(nResult.me.notifications?.edges || []),
              ],
            },
          },
        };
      },
    });
  };

  const handleMarkAsRead = async (notificationId: string, read: boolean) => {
    try {
      await readNotification({ variables: { notificationId, read } });
      await refetch();
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  return (
    <div>
      <NotificationsList
        notifications={data?.me?.notifications?.edges || []}
        onMarkAsRead={handleMarkAsRead}
        loading={loading}
        error={
          error?.graphQLErrors?.[0]
            ? new Error(error.graphQLErrors[0].message)
            : undefined
        }
        hasMore={data?.me?.notifications?.pageInfo?.hasPreviousPage || false}
        onLoadMore={loadMore}
      />
    </div>
  );
};

const NotificationItem: React.FC<{
  notification: NotificationItem;
  onMarkAsRead: (id: string, read: boolean) => void;
}> = ({ notification, onMarkAsRead }) => {
  if (!notification.node.ui) return null;

  const { theme, text } = getNotificationDisplay(
    notification.node.eventId as NotificationEventId,
  );

  const handleNotificationClick = () => {
    try {
      window.open(notification.node.ui!.url, "_self");
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors ${
        notification.node.isRead
          ? "opacity-75"
          : "bg-blue-50/50 dark:bg-blue-950/20"
      }`}
      onClick={handleNotificationClick}
    >
      <div className="grid grid-cols-[auto_120px_1fr] gap-3 items-center w-full">
        <Checkbox
          checked={notification.node.isRead}
          onCheckedChange={(checked) => {
            onMarkAsRead(notification.node.id, checked as boolean);
          }}
          onClick={(e) => e.stopPropagation()}
        />

        <div className="w-40 flex-shrink-0">
          <span
            className={`inline-block w-full text-center ring-1 p-2 rounded-sm max-w-24 ml-3 text-xs ${themeColorMap[theme] || themeColorMap.blue}`}
          >
            {text}
          </span>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <div className="text-sm text-muted-foreground flex-1 truncate">
            {notification.node.ui.textForUrl}
          </div>

          <Button
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              try {
                window.open(
                  notification.node.ui!.url,
                  "_blank",
                  "noopener,noreferrer",
                );
              } catch (error) {
                captureException(error);
              }
            }}
            className="p-1 h-auto flex-shrink-0 bg-transparent hover:bg-transparent"
            size="sm"
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const NotificationSkeleton: React.FC = () => (
  <div className="flex items-start gap-3 p-3 border-b border-border last:border-b-0">
    <div className="w-3 h-3 rounded-full bg-muted animate-pulse flex-shrink-0 mt-1" />
    <div className="flex-1 min-w-0 space-y-2">
      <div
        className="h-3 bg-muted rounded animate-pulse"
        style={{ width: "60px" }}
      />
      <div
        className="h-3 bg-muted rounded animate-pulse"
        style={{ width: "120px" }}
      />
    </div>
  </div>
);

const NotificationsList: React.FC<{
  notifications: NotificationItem[];
  onMarkAsRead: (id: string, read: boolean) => void;
  loading: boolean;
  error?: Error | null;
  hasMore: boolean;
  onLoadMore: () => void;
}> = ({ notifications, onMarkAsRead, loading, error, hasMore, onLoadMore }) => {
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="text-sm text-red-500">
          Something went wrong: {error.message}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-h-80 overflow-y-auto">
        {Array.from({ length: 3 }).map((_, i) => (
          <NotificationSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!notifications.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-8 text-center min-h-[200px] w-full">
        <div className="bg-muted/50 rounded-lg p-8 w-full max-w-xs">
          <div className="text-sm text-muted-foreground font-medium">
            No notifications
          </div>
          <div className="text-xs text-muted-foreground/70 mt-1">
            You're all caught up!
          </div>
        </div>
      </div>
    );
  }

  const sortedNotifications = orderBy(
    notifications,
    (notification) => new Date(notification.node.created),
    "desc",
  );

  return (
    <div className="max-h-80 overflow-y-auto">
      {sortedNotifications.map((notification) => (
        <NotificationItem
          key={notification.node.id}
          notification={notification}
          onMarkAsRead={onMarkAsRead}
        />
      ))}
      {hasMore && (
        <div className="p-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onLoadMore();
            }}
            className="w-full"
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

const NotificationPopover = () => {
  const [selectNotificationType, setSelectNotificationType] =
    useState<string>("All Notifications");
  const [isOpen, setIsOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectingRef = useRef(false);
  const lastSelectTimeRef = useRef<number | null>(null);

  const eventIdsByMessageType = getEventIdsByMessageType();

  const parentVariables = {
    eventIds: [],
    limit: 1,
  };

  const { data: parentData } = useQuery<
    NotificationsQueryQuery,
    NotificationsQueryQueryVariables
  >(NOTIFICATIONS_QUERY, {
    variables: parentVariables,
    errorPolicy: "all",
  });

  const handleFilterChange = (filter: string) => {
    setSelectNotificationType(filter);
    setIsSelectOpen(false);
  };
  const childRefetchRef = useRef<null | (() => void)>(null);

  if (!parentData?.me) {
    return (
      <div className="cursor-pointer">
        <img
          src={BellIcon}
          alt="notification icon"
          className="invert dark:invert-0 h-4 w-4"
        />
      </div>
    );
  }

  const { notificationsUnreadCount: unreadCount } = parentData.me;

  const handleChildRefetchReady = (fn: () => void) => {
    childRefetchRef.current = fn;
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && (isSelectOpen || selectingRef.current)) {
          return;
        }

        if (open && lastSelectTimeRef.current) {
          const delta = Date.now() - lastSelectTimeRef.current;
          if (delta < 500) {
            return;
          }
        }

        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <span className="py-[4px] px-[8px] rounded-[8px] border border-accent-foreground hover:bg-accent shadow-[0px_1px_2px_0px_#1018280A] cursor-pointer">
          <button
            className="relative p-0 m-0 border-0 bg-transparent shadow-none cursor-pointer hover:opacity-70 transition-opacity"
            aria-label="Notifications"
          >
            <img
              src={BellIcon}
              alt="notification icon"
              className="invert dark:invert-0 h-4 w-4 mt-1"
            />

            {!!unreadCount && (
              <span className="absolute p-0.5 rounded-full -top-4 -right-3 bg-paint w-5 h-5 text-xs flex items-center justify-center overflow-hidden">
                {unreadCount > 10 ? "10+" : unreadCount}
              </span>
            )}
          </button>
        </span>
      </PopoverTrigger>

      <PopoverContent
        className="mt-2 w-2xl"
        sideOffset={8}
        align="end"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          setIsOpen(false);
        }}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement;
          if (
            target.closest("[role='listbox']") ||
            target.closest("[role='option']") ||
            target.closest("[role='combobox']") ||
            isSelectOpen
          ) {
            event.preventDefault();
            return;
          }
        }}
      >
        <div className="w-full">
          <div className="text-lg font-semibold mb-2">Notifications</div>

          <Separator />

          <div
            className="flex items-center justify-between my-4 gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              value={selectNotificationType}
              onValueChange={handleFilterChange}
              open={isSelectOpen}
              onOpenChange={setIsSelectOpen}
            >
              <SelectTrigger className="ring-1 p-3 ring-accent-foreground">
                <img
                  src={BellIcon}
                  alt="notification icon"
                  className="invert dark:invert-0 h-4 w-4"
                />
                {selectNotificationType}
              </SelectTrigger>

              <SelectContent
                className="max-h-48"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <SelectGroup>
                  <SelectItem value="All Notifications">
                    All Notifications
                  </SelectItem>

                  {Object.keys(eventIdsByMessageType).map((msg) => (
                    <SelectItem value={msg} key={msg}>
                      {msg}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <div className="mb-2">
              <MarkAllAsRead
                unReadCount={parentData?.me?.notificationsUnreadCount ?? 0}
                refetch={() => {
                  if (childRefetchRef.current) childRefetchRef.current();
                }}
              />
            </div>
          </div>

          <Separator />

          <div>
            <NotificationsFetcher
              eventIds={eventIdsByMessageType[selectNotificationType] || []}
              onRefetchReady={handleChildRefetchReady}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationPopover;
